import { useAppStateDispatch } from '@src/providers/AppStateProvider';
import { useDisplaySettings } from '@src/providers/DisplaySettingsProvider';
import {
  DepthTypeEnum,
  DerivativeSettlementTypeEnum,
  EMPTY_ARRAY,
  FeeModeEnum,
  LiquidityTypeEnum,
  MARKET_DATA_SNAPSHOT,
  MARKET_DATA_STATISTICS,
  OptionTypeEnum,
  POSITION,
  SecurityTypesUtils,
  getOptionCoin,
  logger,
  useObservable,
  useSecuritiesContext,
  useSubscription,
  type IPositionUpdate,
  type MarketDataSnapshot,
  type MarketDataStatistics,
  type SecurityTypes,
  type SubscriptionResponse,
} from '@talos/kyoko';
import Big from 'big.js';
import { compact, get, groupBy, set, uniqBy } from 'lodash-es';
import { useEffect, useMemo } from 'react';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  merge,
  scan,
  shareReplay,
  withLatestFrom,
  type Observable,
} from 'rxjs';
import { getPriceSpread } from '../../../../../components/Ladder/utils';
import { updateMarketDataError } from '../Filter/filterSlice';
import type { OptionFilter, StrikeFilter } from '../Filter/types';
import {
  ALL_EXPIRIES_ITEM,
  CUSTOM_OPTION_RESET_TAG,
  OPTION_BID_PRICE,
  OPTION_BID_SIZE,
  OPTION_OFFER_PRICE,
  OPTION_OFFER_SIZE,
  OPTION_POSITION,
  OPTION_SPREAD,
  type OptionChainDataRow,
} from '../types';
import { getOptionSecurityRowID } from '../utils';

interface UseOptionChainDataProps {
  optionFilter: OptionFilter;
  strikeFilter: StrikeFilter;
  positionsSubscription: Observable<SubscriptionResponse<IPositionUpdate, string>>;
  tag: string;
  panelID: string;
}

export interface UnderlyingPriceUpdate {
  underlying: {
    value: string;
    currency: string;
  };
  BaseCurrency: string;
  Expiration: string;
}

export function useOptionChainData({
  optionFilter,
  strikeFilter,
  positionsSubscription,
  tag,
  panelID,
}: UseOptionChainDataProps) {
  const { showAllInPrices } = useDisplaySettings();
  const { options, securitiesBySymbolObs } = useSecuritiesContext();

  // List of options to request data from.
  const filteredOptionsAtExpiry = useMemo(() => {
    const optionsAtExpiry = getOptionsAfterFilter(options, optionFilter);

    if (strikeFilter.atm || strikeFilter.delta) {
      // We can't filter based on data when requesting the data
    } else if (strikeFilter.strikePrice) {
      const from = strikeFilter.strikePrice.from;
      const to = strikeFilter.strikePrice.to;

      return optionsAtExpiry.filter(opt => {
        // from > strike -> return false
        if (from && Big(from).gt(opt.StrikePrice)) {
          return false;
        }
        // strike > to -> return false
        if (to && Big(opt.StrikePrice).gt(to)) {
          return false;
        }
        return true;
      });
    }
    return optionsAtExpiry;
  }, [options, strikeFilter, optionFilter]);

  const filteredOptionSymbols = useMemo(
    () => new Set(filteredOptionsAtExpiry.map(option => option.Symbol)),
    [filteredOptionsAtExpiry]
  );

  const statisticsRequest = useMemo(() => {
    return filteredOptionsAtExpiry.flatMap(option => [
      {
        name: MARKET_DATA_STATISTICS,
        tag: `${tag}/useOptionChainData`,
        Markets: option.Markets,
        Symbol: option.Symbol,
      },
    ]);
  }, [filteredOptionsAtExpiry, tag]);

  const snapshotRequest = useMemo(() => {
    const selectedMarketAccounts = optionFilter.marketAccountName ? [optionFilter.marketAccountName] : undefined;
    return filteredOptionsAtExpiry.flatMap(option => [
      {
        name: MARKET_DATA_SNAPSHOT,
        tag: `${tag}/useOptionChainData`,
        DepthType: DepthTypeEnum.Price,
        Markets: selectedMarketAccounts ?? option.Markets,
        Symbol: option.Symbol,
        LiquidityType: LiquidityTypeEnum.Indicative,
        UnifiedLiquidity: 'Disabled',
        Depth: 1,
        FeeMode: showAllInPrices ? FeeModeEnum.Taker : undefined,
      },
    ]);
  }, [filteredOptionsAtExpiry, showAllInPrices, optionFilter.marketAccountName, tag]);

  const { data: statisticsSubscription, push } = useSubscription<MarketDataStatistics>(statisticsRequest);
  const { data: snapshotsSubscription, error: snapshotErrorObs } = useSubscription<MarketDataSnapshot>(snapshotRequest);

  const optionDispatch = useAppStateDispatch();
  // Update filter state with error from snapshots, for cme, it can be limited to sessions per user.
  useEffect(() => {
    const subscription = snapshotErrorObs
      .pipe(
        // Since we have multiple requests, we are only interested when the error msg changes (or becomes undefined)
        distinctUntilChanged((a, b) => {
          return a?.error?.msg === b?.error?.msg;
        })
      )
      .subscribe(maybeSnapshotError => {
        optionDispatch(updateMarketDataError({ error: maybeSnapshotError?.error?.msg, panelID }));
      });
    return () => subscription.unsubscribe();
  }, [optionDispatch, snapshotErrorObs, panelID]);

  const underlyingPriceSubscription = useObservable<UnderlyingPriceUpdate | undefined>(
    () =>
      combineLatest([statisticsSubscription, securitiesBySymbolObs])
        .pipe(
          // Get the underlying price from the MD statistic
          map(([mdStatistic, securityBySymbol]) => {
            const statistic = mdStatistic.data.at(0);
            if (!statistic || !statistic.UnderlyingPrice) {
              return undefined;
            }

            const optionSecurity = securityBySymbol.get(statistic.Symbol);
            if (!optionSecurity || !optionSecurity.Expiration || !optionSecurity.BaseCurrency) {
              return undefined;
            }

            const currency = optionSecurity.UnderlyingQuoteCurrency || 'USD';
            const baseCurrency = optionSecurity.BaseCurrency;

            return {
              underlying: {
                value: statistic.Market === 'deribit' ? statistic.IndexPrice : statistic.UnderlyingPrice,
                currency,
              },
              BaseCurrency: baseCurrency,
              Expiration: optionSecurity.Expiration,
            };
          })
        )
        // For MDStats, we only want to update when the underlying price exists.
        .pipe(filter(maybeUnderlying => !!maybeUnderlying)),
    [statisticsSubscription, securitiesBySymbolObs]
  );

  const expiryToStrikePriceToIndex: Map<string, Map<string, number>> = useMemo(() => {
    const expirationAgnosticOptions = getOptionsAfterFilter(options, {
      expiry: ALL_EXPIRIES_ITEM,
      currency: optionFilter.currency,
      market: optionFilter.market,
      underlyingCode: optionFilter.underlyingCode,
      settlementType: optionFilter.settlementType,
      settlementCurrency: optionFilter.settlementCurrency,
    });

    const grouped = groupBy(expirationAgnosticOptions, opt => opt.Expiration);
    const internalMap = new Map<string, Map<string, number>>();

    for (const expiry of Object.keys(grouped)) {
      const sortedStrikes = uniqBy(grouped[expiry], opt => opt.StrikePrice).toSorted((a, b) =>
        Big(a.StrikePrice).cmp(b.StrikePrice)
      );
      const strikeToIndex = new Map(sortedStrikes.map((opt, idx) => [opt.StrikePrice, idx]));
      internalMap.set(expiry, new Map(strikeToIndex));
    }

    return internalMap;
  }, [
    options,
    optionFilter.currency,
    optionFilter.market,
    optionFilter.underlyingCode,
    optionFilter.settlementType,
    optionFilter.settlementCurrency,
  ]);

  /**
   * This effects ensures that whenever the available options optionfilter selection
   * changes, we push a list of all symbols (representing all possible strikes).
   * Effectively (together with the tag) it resets the values for all strikes to what is
   * in the scanned map.
   */
  useEffect(() => {
    const expirationAgnosticOptions = getOptionsAfterFilter(options, {
      expiry: ALL_EXPIRIES_ITEM,
      currency: optionFilter.currency,
      market: optionFilter.market,
      underlyingCode: optionFilter.underlyingCode,
      settlementType: optionFilter.settlementType,
      settlementCurrency: optionFilter.settlementCurrency,
    });

    const newRows = expirationAgnosticOptions.map(opt => ({
      Symbol: opt.Symbol,
    })) as MarketDataStatistics[];
    push({
      data: newRows,
      tag: CUSTOM_OPTION_RESET_TAG,
      type: MARKET_DATA_STATISTICS,
      ts: new Date().toISOString(),
    });
  }, [
    push,
    options,
    optionFilter.currency,
    optionFilter.market,
    optionFilter.underlyingCode,
    optionFilter.settlementType,
    optionFilter.settlementCurrency,
  ]);

  /**
   * Merges subscriptions and creates OptionChainDataRows.
   * 1. MarketDataSnapshot
   * 2. MarketDataStatistics
   * 3. IPositionUpdate
   *
   * To ensure that updates are not partial, a `scan` operator is used, which tracks the state of all data received
   * so far, as well as sending a list of IDs which is used for creating synthetic "delta"-updates.
   */
  const dataObservable: Observable<{
    data: OptionChainDataRow[];
    initial: boolean;
  }> = useObservable(
    () =>
      merge(statisticsSubscription, snapshotsSubscription, positionsSubscription).pipe(
        withLatestFrom(securitiesBySymbolObs),
        scan(
          ({ memo }, [json, optionSecurityBySymbol]) => {
            /**
             * Keep track of what rowIDs that are affected by the particular `json` message.
             */
            const updatedRowIDs: string[] = [];
            if (isIPositionUpdate(json)) {
              if (json.initial) {
                // If new position update with initial:true, we reset position data in the grid.
                const rowIds = Array.from(memo.keys());
                for (const rowID of rowIds) {
                  const prev = memo.get(rowID)!;
                  if (
                    get(prev, [OptionTypeEnum.Call, OPTION_POSITION]) ||
                    get(prev, [OptionTypeEnum.Put, OPTION_POSITION])
                  ) {
                    updatedRowIDs.push(rowID);
                    set(prev, [OptionTypeEnum.Call, OPTION_POSITION], undefined);
                    set(prev, [OptionTypeEnum.Put, OPTION_POSITION], undefined);
                  }
                }
              }

              // Add positions to memo
              for (const d of json.data) {
                const security = optionSecurityBySymbol.get(d.Asset);
                if (!SecurityTypesUtils.Option.isType(security)) {
                  // The position does not map to a valid security, be it expired or incorrect.
                  // Do nothing with it.
                  continue;
                }
                if (!filteredOptionSymbols.has(security.Symbol)) {
                  // Positions are account-wide; only project the currently selected option variant into the shared row.
                  continue;
                }

                const rowID = getOptionSecurityRowID(security);
                const prev = memo.get(rowID) || ({} as Partial<OptionChainDataRow>);
                const side = security.OptionType;
                updatedRowIDs.push(rowID);
                memo.set(rowID, {
                  ...prev,
                  rowID,
                  optionCoin: getOptionCoin(security),
                  StrikePrice: security.StrikePrice,
                  Expiration: security.Expiration,
                  Market: security.Markets?.at(0),
                  UnderlyingQuoteCurrency: security.UnderlyingQuoteCurrency,
                  DerivativeSettlementType: security.DerivativeSettlementType,
                  SettlementCurrency: security.SettlementCurrency,
                  [side]: { ...prev[side], [OPTION_POSITION]: d.Amount },
                });
              }
            } else if (isMarketData(json)) {
              for (const d of json.data) {
                const security = optionSecurityBySymbol.get(d.Symbol);
                if (!SecurityTypesUtils.Option.isType(security)) {
                  logger.error(
                    new Error(
                      `Security ${d.Symbol} does not exist or does not have a strike price: ${security?.StrikePrice}`
                    )
                  );
                  continue;
                }

                const rowID = getOptionSecurityRowID(security);
                updatedRowIDs.push(rowID);
                const side = security.OptionType;
                const prev = memo.get(rowID) || ({} as Partial<OptionChainDataRow>);

                const bid = d.Bids.at(0);
                const bidPrice = bid?.Price;
                const bidSize = bid?.Size;
                const offer = d.Offers.at(0);
                const offerPrice = offer?.Price;
                const offerSize = offer?.Size;

                const spread = getPriceSpread({ bidPrice, offerPrice });

                memo.set(rowID, {
                  ...prev,
                  rowID,
                  Currency: security.BaseCurrency,
                  optionCoin: getOptionCoin(security),
                  QuoteCurrency: security.QuoteCurrency,
                  StrikePrice: security.StrikePrice,
                  Market: security.Markets?.at(0),
                  Expiration: security.Expiration,
                  UnderlyingQuoteCurrency: security.UnderlyingQuoteCurrency,
                  DerivativeSettlementType: security.DerivativeSettlementType,
                  SettlementCurrency: security.SettlementCurrency,
                  [side]: {
                    ...prev[side],
                    [OPTION_SPREAD]: spread,
                    [OPTION_BID_SIZE]: bidSize,
                    [OPTION_BID_PRICE]: bidPrice,
                    [OPTION_OFFER_SIZE]: offerSize,
                    [OPTION_OFFER_PRICE]: offerPrice,
                    Symbol: d.Symbol,
                  },
                });
              }
            } else if (isStatistic(json)) {
              for (const d of json.data) {
                const security = optionSecurityBySymbol.get(d.Symbol);
                if (!SecurityTypesUtils.Option.isType(security)) {
                  logger.error(
                    new Error(
                      `Security ${d.Symbol} does not exist or does not have a strike price: ${security?.StrikePrice}`
                    )
                  );
                  continue;
                }
                const rowID = getOptionSecurityRowID(security);
                updatedRowIDs.push(rowID);
                const side = security.OptionType;

                if (json.tag === CUSTOM_OPTION_RESET_TAG) {
                  const prev = memo.get(rowID) || ({} as Partial<OptionChainDataRow>);
                  memo.set(rowID, {
                    ...prev,
                    rowID,
                    Currency: security.BaseCurrency,
                    optionCoin: getOptionCoin(security),
                    QuoteCurrency: security.QuoteCurrency,
                    Expiration: security.Expiration,
                    StrikePrice: security.StrikePrice,
                    Market: security.Markets?.at(0),
                    UnderlyingQuoteCurrency: security.UnderlyingQuoteCurrency,
                    DerivativeSettlementType: security.DerivativeSettlementType,
                    SettlementCurrency: security.SettlementCurrency,
                    [side]: { ...prev[side], Symbol: security.Symbol },
                  });
                } else {
                  const prev = memo.get(rowID) || ({} as Partial<OptionChainDataRow>);
                  memo.set(rowID, {
                    ...prev,
                    rowID,
                    Currency: security.BaseCurrency,
                    optionCoin: getOptionCoin(security),
                    QuoteCurrency: security.QuoteCurrency,
                    Expiration: security.Expiration,
                    StrikePrice: security.StrikePrice,
                    Market: security.Markets?.at(0),
                    UnderlyingPrice: d.UnderlyingPrice,
                    UnderlyingQuoteCurrency: security.UnderlyingQuoteCurrency,
                    DerivativeSettlementType: security.DerivativeSettlementType,
                    SettlementCurrency: security.SettlementCurrency,
                    [side]: {
                      ...prev[side],
                      ...d,
                      Symbol: d.Symbol,
                      // Explicitly set IVs always, to clear value if undefined
                      BestBidIV: d.BestBidIV,
                      BestOfferIV: d.BestOfferIV,
                      Mark: {
                        MarkPrice: d.MarkPrice,
                        MarkPriceIV: d.MarkPriceIV,
                      },
                    },
                  });
                }
              }
            } else {
              const _exhaustiveCheck: never = json;
              logger.error(new Error(`Option data subscription is getting incorrect data`), {
                extra: json,
              });
            }

            // `initial` is set to true to clear the grid of other strikes.
            return { memo, updatedRowIDs, initial: json.tag === CUSTOM_OPTION_RESET_TAG };
          },
          {
            memo: new Map<string, OptionChainDataRow>(),
            updatedRowIDs: [] as string[],
            initial: false,
          }
        ),
        map(({ memo, updatedRowIDs, initial }) => {
          const deltaUpdates = compact(updatedRowIDs.map(id => memo.get(id)));
          return { data: deltaUpdates ?? [], initial };
        }),
        shareReplay({
          bufferSize: 1,
          refCount: true,
        })
      ),
    [statisticsSubscription, snapshotsSubscription, positionsSubscription, securitiesBySymbolObs, filteredOptionSymbols]
  );
  return { dataObservable, underlyingPriceSubscription, expiryToStrikePriceToIndex };
}

export const getOptionsAfterFilter = (
  allOptions: SecurityTypes['Option'][],
  optionFilter: Partial<
    Pick<OptionFilter, 'currency' | 'expiry' | 'market' | 'underlyingCode' | 'settlementType' | 'settlementCurrency'>
  >
) => {
  return allOptions.filter(option => {
    const markets = option.Markets || EMPTY_ARRAY;
    return (
      (!optionFilter.expiry ||
        optionFilter.expiry === option.Expiration ||
        optionFilter.expiry === ALL_EXPIRIES_ITEM) &&
      (!optionFilter.currency || optionFilter.currency === option.BaseCurrency) &&
      (!optionFilter.market || markets.includes(optionFilter.market)) &&
      (!optionFilter.underlyingCode || getOptionCoin(option) === optionFilter.underlyingCode) &&
      // Settlement narrowing (STS Digital). No-op when settlementType is unset (all other markets).
      // Cash variants are disambiguated by SettlementCurrency; Physical ignores it.
      (!optionFilter.settlementType ||
        (option.DerivativeSettlementType === optionFilter.settlementType &&
          (optionFilter.settlementType === DerivativeSettlementTypeEnum.Physical ||
            !optionFilter.settlementCurrency ||
            option.SettlementCurrency === optionFilter.settlementCurrency)))
    );
  });
};

const isMarketData = (
  res: SubscriptionResponse<MarketDataStatistics | MarketDataSnapshot | IPositionUpdate, string>
): res is SubscriptionResponse<MarketDataSnapshot, string> => res.type === MARKET_DATA_SNAPSHOT;

const isStatistic = (
  res: SubscriptionResponse<MarketDataStatistics | MarketDataSnapshot | IPositionUpdate, string>
): res is SubscriptionResponse<MarketDataStatistics, string> => res.type === MARKET_DATA_STATISTICS;

const isIPositionUpdate = (
  res: SubscriptionResponse<MarketDataStatistics | MarketDataSnapshot | IPositionUpdate, string>
): res is SubscriptionResponse<IPositionUpdate, string> => res.type === POSITION;
